import { generateId } from 'utils/generateId';

export const MAX_COLUMNS_PER_TABLE = 6;
export const MIN_COLUMNS_PER_TABLE = 2;
export const MIN_ROWS_PER_TABLE = 2;
export interface EditableCell {
  rowId: string;
  cellId: string;
  colSpan: number;
}

export interface TableCell {
  id: string;
  content: string;
  backgroundColor?: string;
  colSpan?: number;
  rowSpan?: number;
  header?: boolean;
  hidden?: boolean;
  headerId?: string;
}

export interface TableColumn {
  id: string;
  header: string;
  cellIds: string[];
  backgroundColor?: string;
  hidden?: boolean;
}

export interface TableRow {
  id: string;
  cellIds: string[];
  hidden?: boolean;
}

export interface TableContent {
  columns: TableColumn[];
  rows: TableRow[];
  cells: Record<string, TableCell>;
}

export const handleAddRow = (
  table: TableContent,
  activeRowId: string,
  position: 'above' | 'below',
  updateTableContent: (tableContent: TableContent) => void
) => {
  const { rows, columns, cells } = table;
  const newRows = [...rows];
  const newColumns = [...columns];
  const newCells = { ...cells };

  const newRowId = generateId();
  const newCellIds = newColumns.map(() => {
    const newCellId = generateId();
    newCells[newCellId] = { id: newCellId, content: `` };
    return newCellId;
  });

  const activeRowIndex = newRows.findIndex(row => row.id === activeRowId);
  const insertIndex = position === 'above' ? activeRowIndex : activeRowIndex + 1;

  newRows.splice(insertIndex, 0, {
    id: newRowId,
    cellIds: newCellIds,
  });

  const updatedColumns = newColumns.map((column, index) => ({
    ...column,
    cellIds: [...column.cellIds, newCellIds[index]],
  }));

  updateTableContent({
    columns: updatedColumns,
    rows: newRows,
    cells: newCells,
  });
};

export const handleAddColumn = (
  table: TableContent,
  activeColumnId: string,
  position: 'before' | 'after',
  updateTableContent: (tableContent: TableContent) => void
) => {
  const { rows, columns, cells } = table;
  const newRows = [...rows];
  const newColumns = [...columns];
  const newCells = { ...cells };

  const newColumnId = generateId();
  const newCellIds: string[] = [];

  for (let i = 0; i < newRows.length; i++) {
    const newCellId = generateId();
    newCellIds.push(newCellId);

    newCells[newCellId] = { id: newCellId, content: `` };
  }

  const activeColumnIndex = newColumns.findIndex(column => column.id === activeColumnId);
  const insertIndex = position === 'before' ? activeColumnIndex : activeColumnIndex + 1;

  const updatedRows = newRows.map((row, rowIndex) => {
    const newCellIdForRow = newCellIds[rowIndex];

    const updatedRow = {
      ...row,
      cellIds: [...row.cellIds.slice(0, insertIndex), newCellIdForRow, ...row.cellIds.slice(insertIndex)],
    };

    return updatedRow;
  });

  newColumns.splice(insertIndex, 0, {
    id: newColumnId,
    header: ``,
    cellIds: newCellIds,
  });

  updateTableContent({
    columns: newColumns,
    rows: updatedRows,
    cells: newCells,
  });
};

export const handleRemoveRow = (
  table: TableContent,
  activeRowId: string,
  updateTableContent: (tableContent: TableContent) => void
) => {
  const { rows, columns, cells } = table;
  const newRows = [...rows];
  const newColumns = columns.map(column => ({ ...column, cellIds: [...column.cellIds] }));
  const newCells = { ...cells };

  const activeRowIndex = newRows.findIndex(({ id }) => id === activeRowId);

  if (activeRowIndex !== -1) {
    const cellIdsToRemove = newRows[activeRowIndex].cellIds;

    newColumns.forEach(column => {
      column.cellIds = column.cellIds.filter(cellId => !cellIdsToRemove.includes(cellId));
    });

    cellIdsToRemove.forEach(cellId => {
      delete newCells[cellId];
    });

    newRows.splice(activeRowIndex, 1);
  }

  updateTableContent({
    columns: newColumns,
    rows: newRows,
    cells: newCells,
  });
};

export const handleRemoveColumn = (
  table: TableContent,
  activeColumnId: string,
  activeCellId: string,
  updateTableContent: (tableContent: TableContent) => void
) => {
  const { rows, columns, cells } = table;
  const newRows = rows.map(row => ({ ...row, cellIds: [...row.cellIds] }));
  const newColumns = [...columns];
  const newCells = { ...cells };

  const isHeader = cells[activeCellId].header;
  const activeColumnIndex = newColumns.findIndex(column => column.id === activeColumnId);

  if (activeColumnIndex !== -1) {
    const cellIdsToRemove = newColumns[activeColumnIndex].cellIds;

    newRows.forEach(row => {
      if (isHeader) {
        row.cellIds.forEach(cellId => {
          newCells[cellId] = { ...newCells[cellId], hidden: false };
        });
      }

      row.cellIds = row.cellIds.filter(cellId => !cellIdsToRemove.includes(cellId));
    });

    cellIdsToRemove.forEach(cellId => {
      const headerId = newCells[cellId].headerId || null;
      if (headerId) {
        const headerCell = newCells[headerId];
        if (headerCell && headerCell.colSpan && headerCell.colSpan > 1) {
          newCells[headerId] = { ...headerCell, colSpan: headerCell.colSpan - 1 };
        }
      }
      delete newCells[cellId];
    });

    newColumns.splice(activeColumnIndex, 1);
  }

  updateTableContent({
    columns: newColumns,
    rows: newRows,
    cells: newCells,
  });
};

export const handleApplyBackgroundColor = (
  table: TableContent,
  editableCell: EditableCell | null,
  color: string,
  isHeader: boolean,
  headerId: string,
  updateTableContent: (tableContent: TableContent) => void
) => {
  if (editableCell) {
    const { cells, columns } = table;
    const newCells = { ...cells };
    const newColumns = [...columns];

    if (isHeader) {
      const updatedColumnIndex = newColumns.findIndex(column => column.id === headerId);

      if (updatedColumnIndex !== -1) {
        const updatedColumn = { ...newColumns[updatedColumnIndex], backgroundColor: color };
        newColumns[updatedColumnIndex] = updatedColumn;
      }
    } else {
      const updatedCell = { ...newCells[editableCell.cellId], backgroundColor: color };
      newCells[editableCell.cellId] = updatedCell;
    }

    updateTableContent({
      ...table,
      cells: newCells,
      columns: newColumns,
    });
  }
};

export const handleAddHeader = (
  table: TableContent,
  activeRowId: string,
  backgroundColor: string,
  updateTableContent: (tableContent: TableContent) => void
) => {
  const { rows, columns, cells } = table;
  const newRows = [...rows];
  const newColumns = [...columns];
  const newCells = { ...cells };

  const newRowId = generateId();
  const newCellIds: string[] = [];

  const colSpan = newColumns.length;

  const firstCellId = generateId();
  newCellIds.push(firstCellId);
  newCells[firstCellId] = { id: firstCellId, content: '', backgroundColor, colSpan, header: true };

  for (let i = 1; i < colSpan; i++) {
    const cellId = generateId();
    newCellIds.push(cellId);
    newCells[cellId] = { id: cellId, content: '', hidden: true, colSpan: 1, headerId: firstCellId };
  }

  const activeRowIndex = newRows.findIndex(row => row.id === activeRowId);
  const insertIndex = activeRowIndex + 1;

  newRows.splice(insertIndex, 0, {
    id: newRowId,
    cellIds: newCellIds,
  });

  const updatedColumns = newColumns.map((column, index) => ({
    ...column,
    cellIds: [...column.cellIds, newCellIds[index]],
  }));

  updateTableContent({
    columns: updatedColumns,
    rows: newRows,
    cells: newCells,
  });
};

export const handleMergeCells = (
  table: TableContent,
  activeRowId: string,
  activeColumnId: string,
  activeCellId: string,
  updateTableContent: (tableContent: TableContent) => void
) => {
  const { columns, rows, cells } = table;
  const activeRowIndex = rows.findIndex(row => row.id === activeRowId);

  if (activeRowIndex === -1) {
    return;
  }

  const activeRow = rows[activeRowIndex];
  const activeColumnIndex = columns.findIndex(column => column.id === activeColumnId);

  if (activeColumnIndex === -1) {
    return;
  }

  const activeCell = cells[activeCellId];
  const activeCellIndex = activeRow.cellIds.indexOf(activeCellId);
  const colSpan = activeCell.colSpan || 1;

  const rightCellId = activeRow.cellIds[activeCellIndex + 1];
  const rightCell = cells[rightCellId];

  const leftCellId = activeRow.cellIds[activeCellIndex - 1];
  const leftCell = cells[leftCellId];

  if (rightCell && !rightCell.hidden) {
    const mergedContent = `${activeCell.content}${rightCell.content}`;

    const updatedCells = {
      ...cells,
      [activeCellId]: { ...activeCell, colSpan: colSpan + (rightCell.colSpan || 1), content: mergedContent },
      [rightCellId]: { ...rightCell, hidden: true },
    };

    updateTableContent({
      ...table,
      cells: updatedCells,
    });
  } else if (leftCell && !leftCell.hidden) {
    const mergedContent = `${leftCell.content}${activeCell.content}`;

    const updatedCells = {
      ...cells,
      [activeCellId]: { ...activeCell, colSpan: colSpan + (leftCell.colSpan || 1), content: mergedContent },
      [leftCellId]: { ...leftCell, hidden: true },
    };

    updateTableContent({
      ...table,
      cells: updatedCells,
    });
  }
};

export const handleUnmergeCells = (
  table: TableContent,
  activeRowId: string,
  activeCellId: string,
  updateTableContent: (tableContent: TableContent) => void
) => {
  const { rows, cells } = table;
  const activeRowIndex = rows.findIndex(row => row.id === activeRowId);

  if (activeRowIndex === -1) {
    return;
  }

  const activeRow = rows[activeRowIndex];

  const updatedCells = { ...cells };

  updatedCells[activeCellId] = { ...updatedCells[activeCellId], colSpan: 1 };

  activeRow.cellIds.forEach(cellId => {
    updatedCells[cellId] = { ...updatedCells[cellId], hidden: false, colSpan: 1 };
  });

  updateTableContent({
    ...table,
    cells: updatedCells,
  });
};
